/**
 * Gate Action Types
 * 
 * Centralized type definitions for the unified CTA gating system.
 * All CTAs site-wide should use these types for consistent attribution.
 */

import type { GateSource, GateEntryMode, PageType } from './gateStrategy';

/**
 * CTA identifiers for tracking and analytics.
 * Each CTA type should have a stable source key for attribution.
 */
export type GateCTAType =
  | 'brochure'
  | 'pricing'
  | 'floorplan'
  | 'virtual_tour'
  | 'site_visit'
  | 'callback'
  | 'contact'
  | 'exit_intent'
  | 'floating_cta'
  | 'whatsapp_widget'
  | 'hero_cta'
  | 'section_cta'
  | 'generic';

/**
 * Modal types for TieredLeadModal - aligned with LeadSource
 */
export type GateModalType =
  | 'brochure'
  | 'price'
  | 'floorplan'
  | 'virtual_tour'
  | 'site_visit'
  | 'callback'
  | 'contact'
  | 'dossier'
  | 'exit_intent'
  | 'whatsapp_widget';

// LeadSource for TieredLeadModal compatibility
import type { LeadSource } from '@/hooks/useLeadSubmit';

/**
 * Stable source keys for attribution.
 * Use these exact strings in analytics and database.
 * 
 * RULES:
 * - Never reuse a source for two different UI locations
 * - Never change existing keys (append new ones)
 * - These appear in: whatsapp_events.source, leads.source, ops dashboards
 */
export const GATE_SOURCES = {
  // Floating CTA variants
  floating_cta_whatsapp: 'floating_cta_whatsapp',
  floating_cta_call: 'floating_cta_call',
  floating_cta_brochure: 'floating_cta_brochure',
  
  // Hero CTAs
  hero_brochure: 'hero_brochure',
  hero_enquiry: 'hero_enquiry',
  hero_contact: 'hero_contact',
  hero_whatsapp: 'hero_whatsapp',
  
  // Section CTAs
  pricing_cta: 'pricing_cta',
  floorplan_cta: 'floorplan_cta',
  tour_cta: 'tour_cta',
  tour_preview: 'tour_preview',
  gallery_cta: 'gallery_cta',
  contact_cta: 'contact_cta',
  
  // Exit intent (page-specific)
  exit_intent: 'exit_intent',
  exit_intent_home: 'exit_intent_home',
  exit_intent_pricing: 'exit_intent_pricing',
  exit_intent_floorplan: 'exit_intent_floorplan',
  exit_intent_tour: 'exit_intent_tour',
  exit_intent_location: 'exit_intent_location',
  exit_intent_gallery: 'exit_intent_gallery',
  
  // Widget quick replies (distinct for attribution)
  widget_quickreply_guidance: 'widget_quickreply_guidance',
  widget_quickreply_pricing: 'widget_quickreply_pricing',
  widget_quickreply_floorplan: 'widget_quickreply_floorplan',
  widget_quickreply_visit: 'widget_quickreply_visit',
  widget_custom_message: 'widget_custom_message',
  
  // Contact section
  contact_section: 'contact_section',
  contact_section_whatsapp: 'contact_section_whatsapp',
  
  // Other sections
  brochure_preview: 'brochure_preview',
  gold_campaign: 'gold_campaign',
  dossier_download: 'dossier_download',
  
  // FB Campaign Landing Page
  fb_campaign_price: 'fb_campaign_price',
  fb_campaign_whatsapp: 'fb_campaign_whatsapp',
  fb_campaign_gold: 'fb_campaign_gold',
  fb_campaign_advisor: 'fb_campaign_advisor',
} as const;

export type GateSourceKey = keyof typeof GATE_SOURCES;

/**
 * Action result from gate execution
 */
export interface GateActionResult {
  executed: boolean;
  action: 'whatsapp' | 'modal' | 'phone' | 'delivered' | 'skipped';
  strategy: {
    tierRequired: 0 | 1 | 2;
    entryMode: GateEntryMode;
    offer: { headline: string; subtitle: string };
    experimentId: string;
    exitIntentEnabled: boolean;
  };
}

/**
 * Configuration for useGateAction hook
 */
export interface GateActionConfig {
  /**
   * CTA type identifier
   */
  cta: GateCTAType;
  
  /**
   * Stable source string for attribution (use GATE_SOURCES keys)
   */
  source: GateSource;
  
  /**
   * TieredLeadModal type to open
   */
  modalType: GateModalType;
  
  /**
   * Custom WhatsApp message template
   */
  whatsappMessageTemplate?: string;
  
  /**
   * Include gold campaign in WhatsApp message
   */
  includeGoldCampaign?: boolean;
  
  /**
   * Skip gating entirely (for non-gated links)
   */
  bypassGate?: boolean;
  
  /**
   * Override tier requirement
   */
  overrideTier?: 0 | 1 | 2;
  
  /**
   * Callback after successful gate execution
   */
  onSuccess?: (result: GateActionResult) => void;
  
  /**
   * Download URL for auto-delivery after unlock
   */
  downloadUrl?: string;
  
  /**
   * Download file name
   */
  downloadName?: string;
  
  /**
   * Force a specific channel regardless of gate strategy.
   * Use 'whatsapp' to always open WhatsApp (e.g., success state buttons).
   * Use 'modal' to always show modal.
   */
  forceChannel?: 'whatsapp' | 'modal';
}

/**
 * Runtime overrides for run() - allows dynamic message templates
 */
export interface GateActionRunOverrides {
  /**
   * Override WhatsApp message template at runtime
   */
  whatsappMessageTemplate?: string;
  
  /**
   * Override gold campaign inclusion at runtime
   */
  includeGoldCampaign?: boolean;
}
